import { useState, useEffect } from 'react'

export interface User {
  id: number
  username: string
  email: string
  full_name: string
  role: 'USER' | 'URZEDNIK' | 'KIEROWNIK' | 'ADMIN' | 'MASTER_ADMIN'
  is_active: boolean
}

export function useUser(token: string | null) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setUser(null)
      setLoading(false)
      return
    }

    const fetchUser = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await fetch('/api/users/me', {
          headers: { 'Authorization': `Bearer ${token}` },
        })

        if (!response.ok) {
          throw new Error('Failed to fetch user data')
        }

        const userData = await response.json()
        setUser(userData)
      } catch (err) {
        console.error('Error fetching user:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    fetchUser()
  }, [token])

  // Permission checking functions
  const canEditReport = (reportAuthorId?: number) => {
    if (!user) return false

    // Admin can edit all reports
    if (['ADMIN', 'MASTER_ADMIN'].includes(user.role)) {
      return true
    }

    // Regular users can only edit their own reports
    return reportAuthorId === user.id
  }

  const canDeleteReport = (reportAuthorId?: number) => {
    if (!user) return false

    // Admin can delete all reports
    if (['ADMIN', 'MASTER_ADMIN'].includes(user.role)) {
      return true
    }

    // Regular users can only delete their own reports
    return reportAuthorId === user.id
  }

  const canEditIssue = (issueAuthorId?: number) => {
    if (!user) return false

    // Admin can edit all issues
    if (['ADMIN', 'MASTER_ADMIN'].includes(user.role)) {
      return true
    }

    // Regular users can only edit their own issues
    return issueAuthorId === user.id
  }

  const canDeleteIssue = (issueAuthorId?: number) => {
    if (!user) return false

    // Admin can delete all issues
    if (['ADMIN', 'MASTER_ADMIN'].includes(user.role)) {
      return true
    }

    // Regular users can only delete their own issues
    return issueAuthorId === user.id
  }

  const canEditRequirement = (requirementAuthorId?: number) => {
    if (!user) return false

    // Admin can edit all requirements
    if (['ADMIN', 'MASTER_ADMIN'].includes(user.role)) {
      return true
    }

    // Regular users can only edit their own requirements
    return requirementAuthorId === user.id
  }

  const canDeleteRequirement = (requirementAuthorId?: number) => {
    if (!user) return false

    // Admin can delete all requirements
    if (['ADMIN', 'MASTER_ADMIN'].includes(user.role)) {
      return true
    }

    // Regular users can only delete their own requirements
    return requirementAuthorId === user.id
  }

  const canApproveReports = () => {
    if (!user) return false
    return ['KIEROWNIK', 'ADMIN', 'MASTER_ADMIN'].includes(user.role)
  }

  const canApproveRequirements = () => {
    if (!user) return false
    return ['KIEROWNIK', 'ADMIN', 'MASTER_ADMIN'].includes(user.role)
  }

  const canManageUsers = () => {
    if (!user) return false
    return ['ADMIN', 'MASTER_ADMIN'].includes(user.role)
  }

  const canManageRealizations = () => {
    if (!user) return false
    return ['URZEDNIK', 'KIEROWNIK', 'ADMIN', 'MASTER_ADMIN'].includes(user.role)
  }

  const canManageSettings = () => {
    if (!user) return false
    return ['ADMIN', 'MASTER_ADMIN'].includes(user.role)
  }

  const canViewLogs = () => {
    if (!user) return false
    return ['ADMIN', 'MASTER_ADMIN'].includes(user.role)
  }

  const canResetUserPassword = () => {
    if (!user) return false
    return user.role === 'MASTER_ADMIN'
  }

  return {
    user,
    loading,
    error,
    // Permission functions
    canEditReport,
    canDeleteReport,
    canEditIssue,
    canDeleteIssue,
    canEditRequirement,
    canDeleteRequirement,
    canApproveReports,
    canApproveRequirements,
    canManageUsers,
    canManageRealizations,
    canManageSettings,
    canViewLogs,
    canResetUserPassword,
  }
}
